import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Navigation from './Navigation';
import useAuth from '../../hooks/useAuth';
import { FaBullseye } from 'react-icons/fa';
import AdminFormLayout from './AdminFormLayout';

function EditMission() {
  const { id } = useParams(); // from route `/edit-mission/:id`
  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'daily',
    point: '',
    duration_days: '',
    is_active: true,
    repeatable: false,
    url_check: '',
    image_url: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const { verifyAndRefreshToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('sessionToken');
      const isValid = await verifyAndRefreshToken(token);
      if (!isValid) return navigate('/loginadmin');

      try {
        const [missionRes, outletRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}admin/get-missions?id=${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}outletlist`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ limit: 100, start: 0 }),
          })
        ]);

        const missionJson = await missionRes.json();
        const outletJson = await outletRes.json();

        if (missionJson.error === '0') {
          const mission = missionJson.missions[0];
          setForm({
            title: mission.title,
            description: mission.description,
            type: mission.type,
            point: mission.point,
            duration_days: mission.duration_days,
            is_active: mission.is_active,
            repeatable: mission.repeatable,
            url_check: mission.url_check || '',
            image_url: mission.image_url || '',
          });
          setPreviewImage(`${process.env.REACT_APP_API_BASE_URL}/uploads/${mission.image_url}`);
          setSelectedOutlet(mission.outlet_id.toString());
        }

        if (Array.isArray(outletJson.results)) {
          setOutlets(outletJson.results);
        }
      } catch (err) {
        console.error('Fetch error:', err);
        Swal.fire('Error', 'Failed to fetch mission or outlet data.', 'error');
      }
    };

    fetchData();
  }, [id, navigate, verifyAndRefreshToken]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setForm((prev) => ({ ...prev, [name]: val }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleUploadImage = async () => {
    const token = localStorage.getItem('sessionToken');
    const formData = new FormData();
    formData.append('image', imageFile);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}admin/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });
      const json = await res.json();
      if (json.error === "0") return json.filename;
      else Swal.fire('Error', json.message, 'error');
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('sessionToken');

    let filename = form.image_url;
    if (imageFile) {
      filename = await handleUploadImage();
      if (!filename) return;
    }

    const missionData = {
      id: Number(id),
      ...form,
      point: Number(form.point),
      duration_days: Number(form.duration_days),
      image_url: filename,
      outlet_id: Number(selectedOutlet),
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}admin/update-mission`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(missionData),
      });

      const json = await res.json();
     
      if (json.error === "0") {
        Swal.fire('Success', 'Mission updated successfully!', 'success');
        navigate('/missions');
      } else {
        Swal.fire('Error', json.message || 'Failed to update mission', 'error');
      }
    } catch (err) {
    
      console.error('Update mission error:', err);
      Swal.fire('Error', 'Failed to update mission', 'error');
    }
  };

  return (
    <div>
      <Navigation />
      <AdminFormLayout
        icon={FaBullseye}
        eyebrow="Engagement program"
        title="Update Mission"
        description="Perbarui misi yang sudah tersedia untuk menjaga ketepatan aturan, poin, dan informasi program member."
        breadcrumbs={[
          { label: 'Dashboard', to: '/dashboardadmin' },
          { label: 'Mission Administration', to: '/missions' },
          { label: 'Update Mission' },
        ]}
        tipsTitle="Mission Notes"
        tips={[
          'Periksa kembali judul, deskripsi, dan tipe misi sebelum menyimpan perubahan.',
          'Sesuaikan durasi, outlet, dan poin agar tetap sejalan dengan program yang berjalan.',
          'Unggah gambar baru hanya jika materi promosi perlu diperbarui.',
        ]}
      >
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Type</Form.Label>
                <Form.Select name="type" value={form.type} onChange={handleChange}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="special">Special</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Point</Form.Label>
                <Form.Control
                  type="number"
                  name="point"
                  value={form.point}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Duration Days</Form.Label>
                <Form.Control
                  type="number"
                  name="duration_days"
                  value={form.duration_days}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Outlet</Form.Label>
                <Form.Select
                  value={selectedOutlet}
                  onChange={(e) => setSelectedOutlet(e.target.value)}
                  required
                >
                  {outlets.map((outlet) => (
                    <option key={outlet.id} value={outlet.id}>
                      {outlet.outlet_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Url Check</Form.Label>
                <Form.Control
                  type="text"
                  name="url_check"
                  value={form.url_check}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Is Active"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Repeatable"
                  name="repeatable"
                  checked={form.repeatable}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Current Image</Form.Label>
                {previewImage ? (
                  <div className="mb-2">
                    <img
                      src={previewImage}
                      alt="Current"
                      style={{ width: '100%', maxHeight: '200px', objectFit: 'cover' }}
                    />
                  </div>
                ) : (
                  <p className="text-muted">No image uploaded.</p>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Upload New Image</Form.Label>
                <Form.Control type="file" accept="image/*" onChange={handleImageChange} />
                <Form.Text className="text-muted">
                  Uploading a new image will replace the current one.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="form-actions">
            <Button type="submit" variant="primary">Save Changes</Button>
            <Button variant="secondary" onClick={() => navigate('/missions')}>Cancel</Button>
          </div>
        </Form>
      </AdminFormLayout>
    </div>
  );
}

export default EditMission;
